import { useState, useEffect, useRef } from 'react';
import { ArrowClockwise } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60_000; // 60s

/**
 * Poll-based update prompt. On boot we fetch /api/version and remember it.
 * Every 60s we poll again — if the value changed, the backend was redeployed
 * → we surface a floating "Update beschikbaar" button. Click clears the SW
 * caches and forces a full reload so the user gets the new bundle.
 */
export default function UpdatePrompt() {
    const [hasUpdate, setHasUpdate] = useState(false);
    const [busy, setBusy] = useState(false);
    const initialRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        const check = async () => {
            try {
                const res = await fetch(`${API}/version`, { cache: 'no-store' });
                const data = await res.json();
                if (cancelled) return;
                if (!data?.version) return;
                if (initialRef.current == null) {
                    initialRef.current = data.version;
                } else if (data.version !== initialRef.current) {
                    setHasUpdate(true);
                }
            } catch { /* ignore network hiccups */ }
        };
        check();
        const t = setInterval(check, POLL_MS);
        return () => { cancelled = true; clearInterval(t); };
    }, []);

    const applyUpdate = async () => {
        if (busy) return;
        setBusy(true);
        try {
            if ('serviceWorker' in navigator) {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map(r => r.unregister().catch(() => {})));
            }
            if ('caches' in window) {
                const keys = await caches.keys();
                await Promise.all(keys.map(k => caches.delete(k).catch(() => {})));
            }
        } catch { /* proceed anyway */ }
        // Force full reload from network — hash-busted bundle will be fetched
        window.location.reload();
    };

    if (!hasUpdate) return null;

    return (
        <div
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] max-w-[92vw]"
            role="alert"
            data-testid="update-prompt"
        >
            <button
                onClick={applyUpdate}
                disabled={busy}
                className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#D4A537] hover:bg-[#E5B84A] text-[#1A202C] font-bold shadow-2xl shadow-black/40 border-2 border-[#E5B84A] transition-all active:scale-[0.98] disabled:opacity-60"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="update-prompt-button"
            >
                <ArrowClockwise size={18} weight="bold" className="group-hover:rotate-45 transition-transform" />
                {busy ? 'Bezig met updaten…' : 'Update beschikbaar — vernieuwen'}
            </button>
        </div>
    );
}
